import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../components/Toast';

export interface Watcher { id: string; user: { id: string; name: string; email: string }; }

export function useTicketCollab(ticketId: string | undefined, initialTags: string[] = []) {
  const { toast } = useToast();
  const [tags, setTags] = useState<string[]>(initialTags);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [watchers, setWatchers] = useState<Watcher[]>([]);

  const load = useCallback(async () => {
    if (!ticketId) return;
    try {
      const [w, t] = await Promise.all([
        api.get(`/tickets/${ticketId}/watchers`),
        api.get('/tickets/tags/all'),
      ]);
      setWatchers(w.data.watchers ?? []);
      setAllTags(t.data.tags ?? []);
    } catch { /* silent */ }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);
  // Reset local tags when navigating to a different ticket (intentionally not keyed on initialTags).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setTags(initialTags); }, [ticketId]);

  const saveTags = async (next: string[]) => {
    if (!ticketId) return;
    setTags(next);
    try {
      const res = await api.put(`/tickets/${ticketId}/tags`, { tags: next });
      setTags(res.data.tags ?? next);
    } catch (e: any) {
      toast(e?.response?.data?.error || 'Failed to update tags', 'error');
    }
  };

  const watch = async (userId?: string) => {
    if (!ticketId) return;
    try {
      const res = await api.post(`/tickets/${ticketId}/watchers`, userId ? { userId } : {});
      setWatchers(res.data.watchers ?? []);
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to add watcher', 'error');
    }
  };

  const unwatch = async (userId: string) => {
    if (!ticketId) return;
    try {
      const res = await api.delete(`/tickets/${ticketId}/watchers/${userId}`);
      setWatchers(res.data.watchers ?? []);
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to remove watcher', 'error');
    }
  };

  return { tags, allTags, watchers, saveTags, watch, unwatch, reload: load };
}
